import { FC, useMemo, useState } from 'react'

import { Button, ButtonSizes, ButtonVariants } from '@inno-ui/button'
import { Chip } from '@inno-ui/chip'
import { TextInput } from '@inno-ui/text-input'
import { useTranslation } from 'react-i18next'
import type { ArtifactSummary } from 'shared/api/forgeApi'

import styles from '../ReviewQueue.module.scss'

interface HostsEditorProps {
  artifact: ArtifactSummary
  // Called with the current draft on every change so the parent can ship it
  // on approve (the parent decides when to actually send).
  onChange: (hosts: string[]) => void
}

// Reads a string[] out of the properties bag (allowed_hosts falls back to the
// auto-detected list the backend stored at save time).
const readHosts = (artifact: ArtifactSummary): string[] => {
  const { allowed_hosts, detected_hosts } = artifact.properties ?? {}
  return allowed_hosts ?? detected_hosts ?? []
}

// Editable per-widget egress allowlist. The draft overrides the server value
// until the parent clears it (typically on mutation success). Falls back to
// the server value when untouched so a queue refresh reflects immediately.
export const HostsEditor: FC<HostsEditorProps> = ({ artifact, onChange }) => {
  const { t } = useTranslation('translation', { keyPrefix: 'widgets.aiAgent' })
  const serverHosts = useMemo(() => readHosts(artifact), [artifact])
  const [draftHosts, setDraftHosts] = useState<string[] | null>(null)
  const [newHost, setNewHost] = useState('')
  const hosts = draftHosts ?? serverHosts

  const commit = (next: string[]) => {
    setDraftHosts(next)
    onChange(next)
  }

  const addHost = () => {
    const h = newHost.trim().toLowerCase()
    if (h && !hosts.includes(h)) commit([...hosts, h])
    setNewHost('')
  }
  const removeHost = (h: string) => commit(hosts.filter(x => x !== h))

  return (
    <section className={styles.hostsPanel}>
      <div className={styles.sectionHead}>
        <span className={styles.sectionTitle}>{t('allowed_hosts_label')}</span>
        {hosts.length > 0 && (
          <span className={styles.count}>{hosts.length}</span>
        )}
      </div>
      {hosts.length === 0 ? (
        <p className={styles.muted}>{t('no_external_hosts')}</p>
      ) : (
        <div className={styles.chips}>
          {hosts.map(h => (
            <Chip key={h} label={h} onClose={() => removeHost(h)} />
          ))}
        </div>
      )}
      <div className={styles.hostAdd}>
        <div className={styles.hostInputWrap}>
          <TextInput
            value={newHost}
            placeholder={t('add_host_placeholder')}
            clearable
            onClear={() => setNewHost('')}
            onChange={e => setNewHost(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault()
                addHost()
              }
            }}
          />
        </div>
        <Button
          variant={ButtonVariants.Light}
          size={ButtonSizes.M}
          label={t('add_host')}
          disabled={!newHost.trim()}
          onClick={addHost}
        />
      </div>
      <p className={styles.muted}>{t('allowed_hosts_hint')}</p>
    </section>
  )
}
